import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Music2, ArrowUpDown, Save, Loader2 } from 'lucide-react';
import { TranspositionSettings } from './TranspositionSettings';
import { PreviewSection } from './PreviewSection';
import type { ParsedLine } from '@/lib/leadsheetParser';
import type { PdfHode } from '@/lib/sang/pdf';
import { transponeringsvalg, type Toneart } from '@/lib/sang/toneart';
import type { VisValg } from '@/lib/sang/visSang';

type Visning = VisValg['visning'];

interface TranspositionToolCardProps {
  transposeBy: string;
  onTransposeByChange: (value: string) => void;
  toneart: Toneart | null;
  erTallsang: boolean;
  visning: Visning;
  onVisningChange: (visning: Visning) => void;
  saving: boolean;
  onSave: () => void;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
  fontSize: string;
  onFontSizeChange: (size: string) => void;
  chordFontSize: number;
  onChordFontSizeChange: (size: number) => void;
  chordColor: string;
  onChordColorChange: (color: string) => void;
  columns: string;
  onColumnsChange: (columns: string) => void;
  tekstOgAkkorder: string;
  linjer: ParsedLine[];
  hode: PdfHode;
  onDownloadPDF: () => void;
}

/** Tall-sang kan vises som tall eller som bokstaver i tonearten. Bokstaver krever at sangen har toneart. */
const VisSomValg: React.FC<{ harToneart: boolean; visning: Visning; onChange: (visning: Visning) => void }> = ({
  harToneart,
  visning,
  onChange,
}) => (
  <div className="space-y-2">
    <div className="flex flex-wrap items-center gap-3">
      <Label>Vis som</Label>
      <ToggleGroup
        type="single"
        variant="outline"
        value={visning}
        onValueChange={(valgt) => valgt && onChange(valgt as Visning)}
      >
        <ToggleGroupItem value="original">Tall</ToggleGroupItem>
        <ToggleGroupItem value="bokstaver" disabled={!harToneart}>
          Bokstaver
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
    {!harToneart && <p className="text-sm text-muted-foreground">Sett toneart på sangen for å vise bokstaver</p>}
    {visning === 'original' && <p className="text-sm text-muted-foreground">Tallene er like i alle tonearter</p>}
  </div>
);

export const TranspositionToolCard: React.FC<TranspositionToolCardProps> = ({
  transposeBy,
  onTransposeByChange,
  toneart,
  erTallsang,
  visning,
  onVisningChange,
  saving,
  onSave,
  settingsOpen,
  onSettingsOpenChange,
  fontSize,
  onFontSizeChange,
  chordFontSize,
  onChordFontSizeChange,
  chordColor,
  onChordColorChange,
  columns,
  onColumnsChange,
  tekstOgAkkorder,
  linjer,
  hode,
  onDownloadPDF,
}) => {
  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-primary to-blue-400" />
      <div className="p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Music2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Transponer til ny toneart</h2>
            <p className="text-sm text-muted-foreground">Forhåndsvis og lagre transponeringer</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Velg toneart section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <ArrowUpDown className="h-5 w-5 text-primary" />
              Velg toneart
            </Label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Select value={transposeBy} onValueChange={onTransposeByChange}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {transponeringsvalg(toneart).map((valg) => (
                      <SelectItem key={valg.semitoner} value={valg.semitoner.toString()}>
                        {valg.tekst}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 sm:items-end">
                <Button
                  className="bg-status-confirmed hover:bg-status-confirmed/90 text-white gap-2 flex-1 sm:flex-none h-11"
                  onClick={onSave}
                  disabled={(transposeBy === '0' && !erTallsang) || saving || !toneart}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline">Lagrer...</span>
                      <span className="sm:hidden">Lagrer</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span className="hidden sm:inline">Lagre transponering</span>
                      <span className="sm:hidden">Lagre</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
            {erTallsang && <VisSomValg harToneart={toneart !== null} visning={visning} onChange={onVisningChange} />}
          </div>

          <Separator className="my-6" />

          <TranspositionSettings
            settingsOpen={settingsOpen}
            onSettingsOpenChange={onSettingsOpenChange}
            fontSize={fontSize}
            onFontSizeChange={onFontSizeChange}
            chordFontSize={chordFontSize}
            onChordFontSizeChange={onChordFontSizeChange}
            chordColor={chordColor}
            onChordColorChange={onChordColorChange}
            columns={columns}
            onColumnsChange={onColumnsChange}
          />
        </div>

        <Separator className="my-6" />

        <PreviewSection
          tekstOgAkkorder={tekstOgAkkorder}
          linjer={linjer}
          hode={hode}
          fontSize={parseInt(fontSize)}
          chordFontSize={chordFontSize}
          chordColor={chordColor}
          columns={columns === '2' ? 2 : 1}
          onDownloadPDF={onDownloadPDF}
        />
      </div>
    </Card>
  );
};

